import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Spin } from 'antd';

import useHeaderTitle from '../../hooks/useHeaderTitle';

import './PaymentCallbackPage.less';

const PaymentCallbackPage = (props) => {
	const history = useHistory();
	const location = useLocation();
	const isMountedRef = useRef(true);

	const { verifyPaymentCallback, resetPage, paymentCallbackPageProps } = props;
	const { order, meta } = paymentCallbackPageProps;

	useHeaderTitle('金飯碗 Gold Rice Bowl', true);

	const { orderId, token } = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return {
			orderId: searchParams.get('order_id'),
			token: searchParams.get('token')
		};
	}, [location.search]);

	// Redirect to not-found if params are missing
	useEffect(() => {
		if (!orderId || !token) {
			history.replace('/not-found');
		}
	}, [orderId, token, history]);

	// Initial payment verification when component mounts
	useEffect(() => {
		isMountedRef.current = true;

		if (orderId && token) {
			verifyPaymentCallback(orderId, token);
		}

		return () => {
			isMountedRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, token]);

	// Handle payment verification result and polling
	useEffect(() => {
		if (!isMountedRef.current) return;

		// Handle 404 error
		if (meta.code === 404) {
			history.replace('/not-found');
			return;
		}

		// If paid, redirect to receipt
		if (order && order.payment_status === 'paid') {
			history.replace(`/receipt?order_id=${orderId}&token=${token}`);
			return;
		}

		// Still pending, poll again in 2 seconds
		if (meta.code === 200 && order && order.payment_status !== 'paid') {
			const timeoutId = setTimeout(() => {
				if (isMountedRef.current) {
					verifyPaymentCallback(orderId, token);
				}
			}, 2000);

			return () => clearTimeout(timeoutId);
		}

		// On error, retry after 2 seconds
		if (meta.code && meta.code !== 200 && meta.code !== 404) {
			const timeoutId = setTimeout(() => {
				if (isMountedRef.current) {
					verifyPaymentCallback(orderId, token);
				}
			}, 2000);

			return () => clearTimeout(timeoutId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order, meta, orderId, token]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			resetPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle case where params are missing (will redirect)
	if (!orderId || !token) {
		return null;
	}

	return (
		<div className="payment-callback-container">
			<div className="payment-callback-content">
				<Spin size="large" />
				<h2 className="payment-callback-title">Verifying Payment</h2>
				<p className="payment-callback-subtitle">
					Please wait while we confirm your payment...
				</p>
			</div>
		</div>
	);
};

export default PaymentCallbackPage;
